import React, { useEffect, useState, useRef, useReducer } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Switch, Route, useHistory, useParams } from 'react-router-dom';
import { useFilterDataQuery, usePaginator, operators } from '../FilterContent';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { FormattedMessage, injectIntl } from 'react-intl';
import { i18nIntl, ease_function, getRemotedbUrl, getLayout, isNotEmpty, isColorBright } from '../../lib/ep.js';
import { usePreview } from '../common/dnd/dndpreview';
import { useCSS } from '../../lib/hooks';

import ActionButton, { SquaredButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import FieldLabel from '../builder/FieldLabel';
import SearchField from '../builder/SearchField';
import Spinner from '../builder/Spinner';

import { DraggableSingleGallery, SingleGallery } from '../front/SingleGallery';
import FloatingLayerPublic from '../front/FloatingLayerPublic';
import VideoEmbed from '../front/VideoEmbed';

import EntriesVotingCart from './EntriesVotingCart';
import LeveledHTMLText from '../front/helpers/LeveledHTMLText';
import ButtonCollection from '../builder/ButtonCollection';
import SaveButton from '../builder/SaveButton';
import ShareEntry from '../front/layers/ShareEntry';
import { stage_categories } from '../../data/bootstrap_stages.js';
import SelectMultiple from '../builder/SelectMultiple.js';
import { localizeFields } from '../builder/DynamicFields.js';
import { HTML5Backend } from 'react-dnd-html5-backend';

const Wrapper = ({ promotion, stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const has_more_participations = props?.has_more_participations ?? false;
	const has_votes_enabled = props?.has_votes_enabled ?? false;
	const is_logged = props?.is_logged ?? false;
	const is_closed = props?.is_closed ?? false;
	const is_pending = props?.is_pending ?? false;
	const is_builder = props?.is_builder ?? false;
	const is_user_enabled_in_ranking = props?.is_user_enabled_in_ranking ?? true;
	const default_description_text = `<h1>${i18n("Contest Gallery")}</h1><p>${i18n("These are the multimedia albums of the participants.")}</p>`;
	const page_introduction = (isNotEmpty(stage, 'main_description')) ? stage.main_description : default_description_text;

	const main_image = (isNotEmpty(stage, 'main_image')) ? stage.full_main_image : false;
	const main_video = (isNotEmpty(stage, 'main_video') && stage.main_video.full_url != undefined) ? stage.main_video : false;
	let display_ranking = (has_votes_enabled && stage.voting_enable_ranking != undefined) ? stage.voting_enable_ranking : false;
	if(!is_user_enabled_in_ranking) display_ranking = false;

	return (
		<div class="flex_cols">
			{(main_image || main_video) && (
				<div class="flex_col col_1">
					<div class="main_media_flex">
						{(!main_video && main_image) && (
							<img src={main_image} alt="" style={{maxWidth:'100%'}} />
						)}

						{(main_video) && (
							<VideoEmbed
								video_url={main_video.full_url}
								video_thumb={main_video.image}
							/>
						)}
					</div>
				</div>
			)}

			<div id="success_contents" class="flex_col col_2">
				<article class="gui_main_text_color_on_bg">
					<LeveledHTMLText>{page_introduction}</LeveledHTMLText>

					{(!has_more_participations && has_votes_enabled && is_logged) && (
						<div class="no_more_participations not_in_bottom">
							<div>
								<span class="icon knk-blocked" />

								<p><strong>{i18n("You ran out of votes")}.</strong></p>

								<NextParticipationMessage
									stage={stage}
									intl={props.intl}
								/>
							</div>
						</div>
					)}

					{(is_closed && has_votes_enabled) && (
						<div class="no_more_participations is_closed not_in_bottom">
							<div>
								<span class="icon knk-blocked" />

								<p><strong>{i18n("The voting period has closed")}.</strong></p>
							</div>
						</div>
					)}

					{(is_pending && has_votes_enabled) && (
						<div class="no_more_participations is_pending not_in_bottom">
							<div>
								<span class="icon knk-blocked" />

								<p><strong>{i18n("The votes remain closed until the voting period begins")}.</strong></p>
							</div>
						</div>
					)}

					{(display_ranking) && (
						<div class="success_actions">
							<div class="entries_btn">
								<ActionLink
									_href={(!is_builder) ? `/gallery/${promotion.id}/ranking/${stage.id}` : null}
								>View ranking</ActionLink>
							</div>
						</div>
					)}
				</article>

				<div class="epBox epModule main_contents_flex">
					{props.children}
				</div>
			</div>
		</div>
	)
};

const MyPreview = ({ user, stage, promotion, ...props }) => {
	const { display, itemType, item, style } = usePreview({ width:150, height:120 });
	if(!display) return null;

	return ReactDOM.createPortal(
		<div class="gui_sortable_item awesome" style={style}>
			<ul>
				<SingleGallery
					intl={props.intl}
					promotion={promotion}
					stage={stage}
					selected_entry={false}
					user={item}
					is_in_cart={true}
					has_voting_sidebar={false}
				/>
			</ul>
		</div>
	, document.getElementsByTagName('body')[0]);
};

const VoteAnimated = ({ display_animation, user, stage, promotion, ...props }) => {
	if(user === false || !display_animation) return null;

	const initial_item = document.getElementById(`gallery_${user.Participation.id}`);
	if(initial_item === null) return null;

	const [finished, setFinished] = useState(false);

	const initial_item_attrs = initial_item.getBoundingClientRect();
	const target_item_attrs = document.getElementById("voting_cart").getBoundingClientRect();

	const pos_x_ini = initial_item_attrs.x + (initial_item_attrs.width - 150) / 2;
	const pos_y_ini = initial_item_attrs.y + (initial_item_attrs.height - 120) / 2;

	const pos_x_end = target_item_attrs.x + (target_item_attrs.width - 150) / 2;
	const pos_y_end = target_item_attrs.y + (target_item_attrs.height - 120) / 2;

	const fps = 60;
	let duration = 0.4 * fps;
	let frame_id = 0;
	let animation_id = null;
	let item_to_animate = null;
	const x_step = (pos_x_end - pos_x_ini) / duration;
	const y_step = (pos_y_end - pos_y_ini) / duration;

	const ease = (frame) => {
		const t = (duration - frame) / duration;
		return frame + t * ease_function('easeInOutCubic')(t);
		// potser es podria fer muliplicant el valor que retorna ease_function() a la distancia que ha de recorrer, més que sumar-li un valor
	};

	const frame = () => {
		if(frame_id == duration) {
			clearInterval(animation_id);
			setFinished(true);
		} else {
			frame_id++;

			item_to_animate.style.top = `${pos_y_ini + ease(frame_id) * y_step}px`;
			item_to_animate.style.left = `${pos_x_ini + ease(frame_id) * x_step}px`;
			item_to_animate.style.opacity = `${(1 - ease(frame_id) / duration)}`;
		}
	};

	useEffect(() => {
		setFinished(false);

		setTimeout(() => {
			clearInterval(animation_id);
			item_to_animate = document.getElementById("animated_gallery");
			animation_id = setInterval(frame, 1000 / fps);
		}, 200);

		return () => {
			clearInterval(animation_id);
		}
	}, [user]);

	if(finished) return null;

	return ReactDOM.createPortal(
		<div id="animated_gallery" class="gui_sortable_item is_animated_vote" style={{left:`${pos_x_ini}px`, top:`${pos_y_ini}px`}}>
			<ul>
				<SingleGallery
					intl={props.intl}
					promotion={promotion}
					stage={stage}
					selected_entry={false}
					user={user}
					is_in_cart={true}
					has_voting_sidebar={false}
				/>
			</ul>
		</div>
	, document.getElementsByTagName('body')[0]);
};

const RouterGallery = (props) => {
	const base_path = `${props.base_route}${props.stage.promotion_id}/${props.stage.id}`;
	
	return (
		<Router basename={base_path}>
			<Switch>
				<Route path="/vote/:selected_entry">
					<SimpleVoteConfirm
						type='inline'
						{...props}
					/>
				</Route>

				<Route path="/:selected_entry?">
					<EntriesMultiGallery {...props} />
				</Route>
			</Switch>
		</Router>
	)
}

const simpleVoteReducer = (state,action) => {
	switch (action.type) {
		case 'SET_ENTRY':
			return {
				...state,
				entry:action.entry
			}
		
		case 'SUCCESS':
			return {
				...state,
				current:'SUCCESS',
				result:action.result
			}
		case 'ERROR':
			return {
				...state,
				current:'ERROR',
				reason:action.reason
			}
	
		default:
			return state;
	}
}

const SimpleVoteConfirmWrapper = (props) => {
	const type = props?.type ?? 'inline';

	if(type == 'layer') {
		return (
			<FloatingLayerPublic
				title={props.title}
				pm={props.promotion}
				backButton={props.close}
				max_width={props.width}
				content_styles={{ paddingBlockEnd:'1rem' }}
			>
				{props.children}
			</FloatingLayerPublic>
		)
	}

	return (
		<div>
			<p><strong>{props.title}</strong></p>

			{props.children}
		</div>
	)
};

export const VoteLoginPreview = ({ candidate, ...props }) => {
	if(!candidate) return null;
	
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<aside class="vote_display_on_login">
			<div>
				<p><strong>{i18n("You are voting for")}:</strong></p>

				<ul class="vote_confirm_preview">
					<SingleGallery
						user={{ Participation:candidate.User }}
						promotion={props.promotion}
						stage={candidate.Stage}
						is_vote_confirm_card={true}
						locale={props.locale}
						intl={props.intl}
					/>
				</ul>
			</div>
		</aside>
	)
};

const NextParticipationMessage = (props) => {
	if(props.children != undefined) {
		return (
			<p class="next_participation_warn">
				{props.children}
			</p>
		)
	}

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = props?.stage ?? false;

	const participation_frequency = stage?.participation_frequency ?? 'total';

	if(participation_frequency == 'total') {
		return null;

		return (
			<p class="next_participation_warn">
				<span>{i18n("You can't vote again for it.")}</span>
			</p>
		)
	}

	let timing_msg = "";
	switch(stage.participation_frequency) {
		case 'hour':
			timing_msg = "in an hour";
			break;

		case 'day':
			timing_msg = "Tomorrow";
			break;

		case 'week':
			timing_msg = "next week";
			break;
	}


	return (
		<p class="next_participation_warn">
			<FormattedMessage
				id="You will be able to vote again {time}."
				values={{ time:i18n(timing_msg, 'lowercase') }}
			/>
		</p>
	)
};

const SimpleVoteConfirm = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ state, dispatch ] = useReducer(simpleVoteReducer, {
		current:(props.participate_error == undefined || props.participate_error.length == 0) ? 'CONFIRM' : 'ERROR',
		entry:props?.entry ?? false,
		reason:props?.participate_error?.reason ?? null,
	});

	let { selected_entry } = useParams();

	const is_voted = props?.is_voted ?? false;

	useEffect(() => {
		if(props.entry != undefined) return;

		const url = getRemotedbUrl(`/stages/getgalleryvoting/${props.stage.promotion_id}/${props.stage.id}`);
		let fetch_params = { start:false, limit:1, offset:false };
		if(selected_entry != undefined) {
			fetch_params.ensure_entry = selected_entry;
		}
		$.get(url, fetch_params, (response) => {
			const _search_entry = response.data.find((user) => user.Participation.id == selected_entry );
			if(_search_entry)dispatch({ type:'SET_ENTRY', entry:_search_entry});
		}, 'JSON');
	}, []);

	const vote = (e, cb) => {
		const is_logged = props?.is_logged ?? false;
		if(!is_logged) {
			window.location.href = `/login/${props.stage.promotion_id}/?vote=${props.stage.id}_${state.entry.Participation.id}`;
			return;
		}

		props.votingSubmit(0, {
			participation_id:state.entry.Participation.id,
			quiz_entry:{
				votes:[{ id:state.entry.StageVote.id, score:1 }],
			}
		}, (result) => {
			if(cb != undefined) cb();

			if(result.status == 1) {
				dispatch({ type:'SUCCESS', result });
				return;
			}

			dispatch({ type:'ERROR', reason:result.reason });
		});
	}

	if(state.entry === false) return null;

	const display_type = props?.type ?? 'layer';

	const wrapper_props = {
		type:display_type,
		promotion:props.promotion,
		close:props.onClose,
	};

	let error_msg = "Try again later";
	let error_submsg = false;

	if(state.current == 'ERROR') {
		switch(state.reason) {
			case 'ip_delay':
				error_msg = "Try again later";
				error_submsg = "Someone already voted for this entry from the same IP.";
				break;

			case 'browser_ip':
				error_msg = "Invalid vote";
				error_submsg = "You exceeded the limit of votes from the same computer.";
				break;

			case 'fraud':
				error_msg = "Invalid vote";
				error_submsg = "A security restriction has been applied.";
				break;

			case 'country':
				error_msg = "Vote area restricted";
				error_submsg = "You are not allowed to vote in this promotion.";
				break;

			case 'participations':
				error_msg = "No more participations";
				error_submsg = "You cannot participate again in this stage.";
				break;

			case 'vote_same_period':
				switch(props.stage.participation_frequency) {
					case 'hour':
					case 'day':
					case 'week':
						error_msg = "You already voted for this entry";
						break;

					case 'total':
					default:
						error_msg = "No more participations";
						break;
				}
				break;
		}
	}

	const has_prize = (state.current == 'SUCCESS') ? (state.result?.prize ?? false) : false;
	const has_share = ((props.promotion.config?.share?.status ?? '1') == '1') ? true : false;

	return (
		<SimpleVoteConfirmWrapper
			key='vote_confirm'
			{...wrapper_props}
			title={`${i18n((!is_voted && state.current != 'SUCCESS') ? "You are voting for" : "You voted for")}:`}
			width='380px'
		>
			<ul class={`vote_confirm_preview${(display_type != 'layer') ? ' is_inline' : ''}`}>
				<SingleGallery
					user={state.entry}
					promotion={props.promotion}
					stage={props.stage}
					is_vote_confirm_card={true}
					locale={props.locale}
					intl={props.intl}
				/>
			</ul>

			{(!is_voted && state.current != 'ERROR' && state.current != 'SUCCESS') && (
				<ButtonCollection
					_class={{ other:'vote_confirm flex_button gui_flex_col gui_flexbox_auto' }}
					style={{ minHeight:'40px' }}
				>
					<SaveButton
						_fullclass=""
						onClick={(!is_voted) ? vote : null}
						disabled={(!is_voted) ? false : true}
						autotranslate={false}
					>{(!is_voted) ? i18n("Confirm") : i18n("voted", 'ucfirst')}</SaveButton>
				</ButtonCollection>
			)}

			{(!is_voted && state.current == 'SUCCESS') && (
				<div class="voting_success">
					<span class="icon ep-check-list-item" />

					<p><strong>{i18n("Thanks for voting!")}</strong></p>
				</div>
			)}

			{(state.current == 'ERROR') && (
				<div class="voting_error">
					<span class="icon knk-alert" />

					<p><strong>{i18n("Oops!")}<br />{i18n(error_msg)}</strong></p>
				</div>
			)}

			{((state.current != 'ERROR' && (is_voted || state.current == 'SUCCESS') && !(state?.result?.retries > 0)) || state.current == 'ERROR') && (
				<NextParticipationMessage
					stage={props.stage}
					intl={props.intl}
				>{(state.current == 'ERROR' && error_submsg !== false) ? i18n(error_submsg) : null}</NextParticipationMessage>
			)}

			{(has_prize) && (
				<div class="prize_message">
					<h4>{i18n("You've won a prize")}</h4>

					<ActionLink
						_href={state.result.url}
					>View prize</ActionLink>
				</div>
			)}

			{(display_type != 'layer' || (has_share && state.current != 'ERROR' && (is_voted || state.current == 'SUCCESS'))) && (
				<div class="small_actions_flex" style={{ marginBlockStart:'1.8em' }}>
					{(has_share && state.current != 'ERROR' && (is_voted || state.current == 'SUCCESS')) && (
						<div class="share_vote">
							<ShareEntry
								promotion={props.promotion}
								stage={props.stage}
								user={state.entry.Participation}
								sharing_type='gallery_voteme'
								icon=''
								type='link'
								label="Share your vote"
							/>
						</div>
					)}

					{(display_type != 'layer') && (
						<div class="back_btn">
							<ActionLink
								_href={`/promotions/participate/${props.promotion.id}/${props.stage.id}`}
							>Go back</ActionLink>
						</div>
					)}
				</div>
			)}
		</SimpleVoteConfirmWrapper>
	)
}

const EntriesMultiGallery = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	useCSS('comp/multigallery.css');

	const stage = props?.stage ?? false;
	if(!stage) return null;

	const promotion = props?.promotion ?? false;
	const tz = promotion.timezone;
	const open = (moment.tz(stage.start_date,tz).isBefore() && moment.tz(stage.end_date,tz).isAfter()) ? true : false;
	const closed = (moment.tz(stage.end_date,tz).isBefore()) ? true : false;
	const pending = (!open && !closed) ? true : false;
	const is_builder = props?.is_builder ?? false;
	const is_logged = props?.is_logged ?? false;
	const login_id = (is_logged && window.login_info) ? window.login_info.login_id : false;
	const avail_participations = (props.avail_participations != undefined) ? props.avail_participations : {};
	const has_more_participations = (avail_participations.next_participation == undefined || avail_participations.next_participation == true) ? true : false;
	const has_votes_enabled = stage?.voting_enabled ?? false;
	//const is_votable = (has_votes_enabled && has_more_participations) ? true : false;
	const is_votable = (has_votes_enabled) ? true : false;
	const simple_voting = (is_votable && (stage?.voting_type ?? 'simple') == 'simple') ? true : false;

	const is_user_logged = ((window?.login_info ?? false) !== false) ? true : false;
	const display_votes_not_logged = ((promotion?.galleries_hide_votes_to_visitors ?? false) === false) ? true : false;
	const is_user_enabled_in_ranking = (!is_user_logged && !display_votes_not_logged) ? false : true;

	const is_voting_opened = (simple_voting) ? (is_votable) : ((is_votable && open && !pending) ? true : false);
	const gallery_columns_per_row = 4;
	const items_per_page = props.promotion.id == 947538 ? 100 : 24;
	const nPage = 100;
	const nPagination = items_per_page;
	const local_key = `votes_${stage.id}`;
	const display_country = stage?.voting_user_country ?? false;

	let { selected_entry } = useParams();
	const history = useHistory();

	const fetched_all = useRef(false);
	const ensure_entry = useRef(false);
	const justFocus = useRef(false);

	useEffect(() => {
		if(!display_country) return;
		if (window?.countries != undefined) return;

		$.get('/v24/utils/getAllCountriesPhonePrefixes', (res) => {
			if(res.length > 0) {
				window.countries = res;
			}
		}, 'JSON');
	}, []);

	const validateExistingCart = (cart) => {
		const original = generateCart();
		if (cart.length != original.length) return original;
		return cart;
	}

	const getUsers = (start = false, limit = 100,offset) => {
		if(limit == 100) setLoading(true);
		let url = false;
		switch(parseInt(stage.type)) {
			case stage_categories.ID_VOTING_CATEGORY.id:
				url = `/stages/getgalleryvoting/${stage.promotion_id}/${stage.id}`;
				break;
		}

		if(!url) return;

		let fetch_params = { start, limit,offset };
		if(selected_entry != undefined) {
			fetch_params.ensure_entry = selected_entry;
			ensure_entry.current = selected_entry;
		}

		return $.getJSON(getRemotedbUrl(url), fetch_params, (res) => {
			const _search_entry = res.data.find((user) => user.Participation.id == selected_entry );
			if(!_search_entry)ensure_entry.current=false;
			
			const tags = localizeFields(promotion?.available_media_tags ?? [], props.locale);
			if((res?.used_media_tags ?? []).length > 0 && tags.length > 0) {
				const _used_media_tags = [];
				res.used_media_tags.map((tag) => {
					const _tag = tags.find((t) => (t.id == tag.id));
					if(_tag == undefined) return;
					if((tag?.total ?? 0) == 0) return;

					_used_media_tags.push({
						..._tag,
						title:`${_tag.title} (${tag.total})`,
					});
				});

				setUsedMediaTags(_used_media_tags);
			}

			setLoading(false);
		});
	};

	const generateCart = () => {
		let vote_array = [];
		switch(stage.voting_type) {
			case 'simple':
				for(var i = 0; i < stage.voting_max; i++) vote_array.push(false);
				break;

			case 'tiered':
			case 'ponderated':
				for(var i in stage.voting_tiers) vote_array.push(false);
				break;
		}

		return vote_array;
	};

	const [ galleryState, dispatchGallery ] = useReducer((state, action) => {
		switch(action.type) {
			case 'ADD_VOTE_CART_SLOT':
				if(stage.voting_type == 'ponderated') {
					let already = state.vote_cart.find((p) => {
						if(p === false) return false;
						return p.Participation.id == action.participant.Participation.id;
					});

					if(already) return state;
				}

				return {...state, vote_cart:state.vote_cart.map((v, pos) => {
					if(pos != action.pos) return v;
					return action.participant;
				})};

			case 'ADD_VOTE_CART':
				let new_cart = [];

				let already = state.vote_cart.find((p) => {
					if(p === false) return false;
					return p.Participation.id == action.participation.Participation.id;
				});

				if(already) return state;

				let added = false;
				for(var i in state.vote_cart) {
					if(state.vote_cart[i] != false) {
						new_cart.push(state.vote_cart[i]);
						continue;
					}

					if(!added) {
						new_cart.push(action.participation);
						added = true;
						continue;
					}

					new_cart.push(state.vote_cart[i]);
				}

				return { ...state, vote_cart:new_cart, animate:action.participation };

			case 'DEL_VOTE_CART':
				let after_delete = state.vote_cart.map((c,pos) => {
					if(c === false) return false;
					let fulfill_position = action.index == undefined || action.index == pos;
					if(c.Participation.id == action.id && fulfill_position) return false;
					return c;
				});

				if(stage.voting_type == 'simple') {
					after_delete.sort((a, b) => {
						if(a === false) return 1;
						if(b === false) return -1;
						return 0;
					});
				}

				return { ...state, vote_cart:after_delete, animate:false };

			case 'SET_LOADING':
				return { ...state, loading:true };

			case 'SET_STARTING_CART':
				let valid_cart = validateExistingCart(action.cart)
				//let valid_cart = action.cart;
				return { ...state, vote_cart:valid_cart };

			case 'SET_DRAG_START':
				if(!action.drag) return state;
				return { ...state, collapsed:false };

			case 'TOGGLE_COLLAPSED':
				return { ...state, animate:false, collapsed:!state.collapsed };

			case 'TOGGLE_ADD_CART_ERROR':
				return { ...state, display_message_vote_self:!state.display_message_vote_self };

			case 'SET_RESPONSE':
				return { ...state, response_on_vote:action.response };

			case 'RETURN_TO_GALLERY':
				return { ...state, response_on_vote:false, loading:false, vote_cart:generateCart(), };

			case 'TOGGLE_SIMPLE_VOTE':
				return { ...state, participation:action.participation, is_voted:action.is_voted };

			default:
				return state;
		}
	}, {
		animate:false,
		collapsed:true,
		current_participation:false,
		participation:false,
		current_media:0,
		loading:false,
		is_voted:false,
		response_on_vote:false,
		vote_cart:generateCart(),
		display_message_vote_self:false,
	});

	const [ loading, setLoading ] = useState(false);
	const [ used_media_tags, setUsedMediaTags ] = useState([]);
	const [ search, setSearch ] = useState("");

	const [ state, dispatch, fetchAll ] = useFilterDataQuery('users', getUsers, {
		indexer:(indexed, element) => {
			return;
		},
		fetch_all_disabled:true,
		items_per_page:items_per_page,
		limit:nPage,
		pagination:nPagination,
	});

	const [ data, pagination, goToPage ] = usePaginator(state.filtered, items_per_page, {
		total:(state.filters.length == 0) ? state.total : state.filtered.length,
		skip_reset_page:(state.filters.length > 0) ? false : true,
	});
	
	useEffect(() => {
		setTimeout(() => {
			if(window.adaptWidgetHeight != undefined) adaptWidgetHeight();
		}, 500);
	}, []);

	useEffect(() => {
		window.addEventListener('focus', visibilityChange);
		document.addEventListener('visibilitychange', visibilityChange);

		visibilityChange();

		return () => {
			document.removeEventListener('visibilitychange', visibilityChange);
			window.removeEventListener('focus', visibilityChange);
		}
	}, [pagination.page]);

	useEffect(() => {
		if(window.localStorage == undefined || is_builder) return;
		let data = window.localStorage.getItem(local_key);
		if(data == null) return;

		dispatchGallery({ type:'SET_STARTING_CART', cart:JSON.parse(data) });
	}, []);

	useEffect(() => {
		if(window.localStorage != undefined) {
			let should_save = false;
			for(var i in galleryState.vote_cart) {
				if(galleryState.vote_cart[i] !== false) {
					should_save = true;
					break;
				}
			}

			if(!should_save) {
				window.localStorage.removeItem(local_key);
			} else {
				window.localStorage.setItem(local_key, JSON.stringify(galleryState.vote_cart));
			}
		}
	}, [ galleryState.vote_cart ]);

	const visibilityChange = () => {
		if (document.visibilityState == 'visible') {
			if(pagination.page != 1) return;

			justFocus.current = true;
			setTimeout(() => {
				justFocus.current = false;
			}, 10000);
		}
	};

	const openDetailLayer = (participation_id) => {
		if(is_builder) return;
		history.push(`/${participation_id}`);
	};

	const closeEntryAsLayer = () => {
		if(is_builder) return;
		history.push(`/`);
	};

	const voteForEntry = (participation, is_voted = false) => {
		if(simple_voting) {
			if(!is_logged) {
				window.location.href = `/login/${props.stage.promotion_id}/?vote=${props.stage.id}_${participation.Participation.id}`;
				return;
			}
			
			dispatchGallery({ type:'TOGGLE_SIMPLE_VOTE', participation, is_voted });
			return
		}

		if(stage.voting_self_unallowed == true && login_id == participation.Participation.login_id) {
			dispatchGallery({ type:'TOGGLE_ADD_CART_ERROR'});
		} else {
			dispatchGallery({ type:'ADD_VOTE_CART', participation });
		}
	};

	const removeFromCart = (participation_id, index) => {
		dispatchGallery({ type:'DEL_VOTE_CART', id:participation_id, index });
	};

	const resetCart = () => {
		dispatchGallery({type:'SET_LOADING'});
		window.localStorage.removeItem(local_key);
	};

	const toggleCollapsed = () => {
		dispatchGallery({ type:'TOGGLE_COLLAPSED' });
	};

	const ensureData = (page) => {

		if(state.total > nPagination)
		if(!fetched_all.current) {
			fetched_all.current = true;
			if(ensure_entry.current != false) {
				if(state.data.length > nPage) {
					dispatch({ type:'REMOVE_ENTRY', uid:ensure_entry.current });
				}
				ensure_entry.current = false;
			}
			// ((page == 2) ? (page-1)*nPage : (page-1)*items_per_page)
			if(parseInt(state.total) > nPagination * (pagination?.page ?? 1)){//No cal fer fetch all si ja no hi ha mes entries
				fetchAll({ last:state.last, offset:nPage });
			}
		}
	};

	const changePage = (page) => {
		let entries = (page) * items_per_page;
		if(entries >= nPage) ensureData(page);
		goToPage(page);
	};

	const onFilterByTags = (selected_tags) => {
		ensureData();

		const filter = {
			key:'search_tag',
			name:"Tags",
			type:'in_array',
			tier1:(user) => {
				return user.Participation.quiz_entry.tags;
			},
		};

		dispatch({
			type:'SETFILTER',
			filter:{
				key:'search_tag',
				value:selected_tags,
				operator:'in_array',
				filter,
			},
			unique:true,
		});
	};

	const onSortEnd = (items) => {
		dispatchGallery({ type:'SET_STARTING_CART', cart:items });
	};

	const voteSubmit = (score, user, cb) => {
		if(props.votingSubmit != undefined) props.votingSubmit(score, user, cb);
		resetCart();
	};

	const simpleVoteSubmit = (score, user, cb) => {
		if(props.votingSubmit != undefined) {
			props.votingSubmit(score, user, (result)=>{
				cb(result);
				if (result.status == 1) {
					const vote = user.quiz_entry.votes[0].id;
					dispatch({
						type:'APPLY_ACTION',
						match:(u) => (u.StageVote.id == vote),
						action:(u) => ({
							...u,
							StageVote:{ ...u.StageVote, already_voted:true,points:parseInt(u.StageVote.points)+1},
						})
					});
				}
			});
		} else {
			window.location.href = `/login/${props.stage.promotion_id}/?vote=${props.stage.id}_${user.participation_id}`;
		}
	}

	const onItemDrop = (participant, pos) => {
		switch(stage.voting_type) {
			case 'simple':
				voteForEntry(participant);
				break;

			case 'tiered':
			case 'ponderated':
			default:
				dispatchGallery({ type:'ADD_VOTE_CART_SLOT', participant, pos });
				break;
		}
	};

	const searchEntries = (k, value) => {
		ensureData();

		const filter = {
			key:'search',
			name:"Name",
			//tier1:['Participation.name','Participation.id'],
			tier1:['Participation.search_media_values'],
			tier2:{ ...operators.textOperators },
			tier3:'input',
			type:'string',
		};

		dispatch({
			type:'SETFILTER',
			filter:{
				key:'search',
				value,
				operator:'like_all',
				filter,
			},
			unique:true,
		});

		setSearch(value);
	};
	const layout_props = getLayout(promotion);
	const is_main_color_bg_light = isColorBright(layout_props.color.main_color);

	const wrapper_props = {
		intl:props.intl,
		is_builder,
		has_more_participations,
		has_votes_enabled,
		promotion,
		stage,
		is_closed:closed,
		is_logged,
		is_pending:pending,
		is_user_enabled_in_ranking,
	};

	if(loading) {
		return (
			<Wrapper {...wrapper_props}>
				<article class="entries_list" style={{minHeight:'200px'}}>
					<Spinner
						autotranslate={false}
						color='var(--text-color)'
					>{i18n("Loading the gallery")}...</Spinner>
				</article>
			</Wrapper>
		)
	}

	if(galleryState.loading) {
		const returnToGallery = () => {
			// TO DO: fer que els comptadors de vots de les participacions s'actualitzin perquè si no sembla que no hagis pogut votar
			//dispatchGallery({ type:'RETURN_TO_GALLERY' });
			window.location.href = `/gallery/${stage.promotion_id}/${stage.id}`;
		};

		let button_label = "Please wait...";
		let button_action = {};
		if(galleryState.response_on_vote !== false) {
			if(galleryState.response_on_vote.prize !== false) {
				button_label = "You've won a prize";
				button_action._href = galleryState.response_on_vote.url;
			} else if(stage.after_voting_action != undefined && stage.after_voting_action == 'thanks') {
				button_label = "Continue";
				button_action._href = galleryState.response_on_vote.url;
			} else {
				button_label = "Back to gallery";
				button_action.onClick = () => returnToGallery();
			}
		}

		const default_thanks_voting_text = '<h1><strong>' + i18n("Done!") + '</strong></h1><p>' + i18n("Thank you for voting.") + '</p>';
		const success_label = (stage.voting_success_label != undefined && stage.voting_success_label != "") ? stage.voting_success_label : default_thanks_voting_text;

		return (
			<div class="flex_cols">
				<div id="success_contents" class="flex_col col_2">
					<div class="epBox epModule main_contents_flex">
						<article class={'submitting_message gui_pos_rel' + ((galleryState.response_on_vote !== false) ? ' is_done' : '')}>
							<article class="template_module gui_main_text_color_on_bg register_text_flex">
								<LeveledHTMLText
									_class={{other:'text_message template_submodule'}}
								>
									<h2><strong>{i18n("Submitting your participation")}</strong></h2>
								</LeveledHTMLText>

								<div class="icon_container">
									<div class="icon knk-check original_color_item" />
								</div>
							</article>

							<div class="gui_voted_process_animation">
								<div class="thanks_message">
									<article class="template_module gui_main_text_color_on_bg register_text_flex">
										<LeveledHTMLText
											_class={{other:'template_submodule'}}
										>{success_label}</LeveledHTMLText>
									</article>
								</div>

								<div class="passing_cover_bg" />

								<ul class="medias_passing_by" style={{width:`calc(100% + 80 * ${(galleryState.vote_cart.length - 1)}px + 40px)`}}>
									{galleryState.vote_cart.map((voted_user, v) => {
										if(voted_user === false) return false;

										return (
											<div key={`voted_${v}`} style={{marginTop:`${(Math.random() * 40 - 20)}px`, marginLeft:`-${v * 80}px`, transform:`rotate(${(Math.random() * 30 - 15)}deg)`}}>
												<SingleGallery
													promotion={promotion}
													stage={stage}
													selected_entry={false}
													user={voted_user}
													is_in_cart={true}
													has_voting_sidebar={false}
													is_builder={is_builder}
													intl={props.intl}
												/>
											</div>
										)
									})}
								</ul>
							</div>
						</article>

						<div class="gallery_continue_btn template_submodule mod_align_center">
							<div class="flex_button gui_big_button">
								<ActionButton
									_fullclass={(galleryState.response_on_vote === false) ? 'disabled_button' : ''}
									{...button_action}
								>{button_label}</ActionButton>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const GalleryComponent = (!simple_voting && is_voting_opened) ? DraggableSingleGallery : SingleGallery;

	const votesCasted = galleryState.vote_cart.map((v) => (v && v.StageVote.id));

	const highlight = (selected_entry != undefined && data.find((u) => u.Participation.id == selected_entry) == undefined) ? true : false;

	const common_gallery_props = {
		closeEntryAsLayer:closeEntryAsLayer,
		has_voting_sidebar:(!simple_voting && is_voting_opened) ? true : false,
		intl:props.intl,
		is_votable:is_voting_opened,
		onDragStart:(drag) => dispatchGallery({ type:'SET_DRAG_START', drag }),
		//onDragStart:() => {},
		has_more_participations,
		is_closed:closed,
		is_pending:pending,
		is_logged,
		openDetailLayer,
		promotion,
		selected_entry,
		stage,
		is_builder,
		votes_casted:votesCasted,
		voteForEntry:voteForEntry,
		locale:props.locale,
	};

	const DND_Backend = window?.is_mobile() ? TouchBackend : HTML5Backend;

	return (
		<DndProvider backend={DND_Backend} options={{enableMouseEvents:is_voting_opened, enableTouchEvents:is_voting_opened}}>
			<Wrapper {...wrapper_props}>
				{(stage?.voting_display_toolbar ?? true) && (
					<aside class="gui_gallery_filter gui_main_text_color_on_bg">
						<div class="total_entries">
							<FieldLabel
								_class={{ first:true, other:'without_colon' }}
								autotranslate={false}
							>{i18n("Total")}: {(state.filters.length == 0) ? state.total : state.filtered.length}</FieldLabel>
						</div>

						<SelectMultiple
							items={used_media_tags}
							onChange={onFilterByTags}
						/>

						<div class="search_form">
							<div>
								<SearchField
									change={searchEntries}
									value={search}
									placeholder="Search"
									name="search"
									_class={{ first:true }}
								/>
							</div>
						</div>
					</aside>
				)}

				{(is_voting_opened && !simple_voting) && (
					<EntriesVotingCart
						avail_participations={avail_participations}
						collapsed={galleryState.collapsed}
						dispatchGallery={dispatchGallery}
						toggleCollapsed={toggleCollapsed}
						is_builder={is_builder}
						onItemDrop={onItemDrop}
						onSortEnd={onSortEnd}
						promotion={promotion}
						removeFromCart={removeFromCart}
						resetCart={resetCart}
						stage={stage}
						users={galleryState.vote_cart}
						votingSubmit={voteSubmit}
						is_logged={is_logged}
						login_id={login_id}
					/>
				)}

				<ul class={`gui_multimedia_gallery has_column_of_${gallery_columns_per_row} gui_main_text_color_on_bg`}>
					{(state.filtered.length > 0 && highlight) && (
						<GalleryComponent
							key={selected_entry}
							user={state.filtered.find((u) => u.Participation.id == selected_entry)}
							is_forced={true}
							{...common_gallery_props}
						/>
					)}

					<MyPreview
						intl={props.intl}
						user={state.filtered[1]}
						promotion={promotion}
						stage={stage}
					/>

					<VoteAnimated
						intl={props.intl}
						display_animation={galleryState.collapsed}
						user={galleryState.animate}
						promotion={promotion}
						stage={stage}
					/>

					{data.map((user) => {
						return (
							<GalleryComponent
								item_id={`gallery_${user.Participation.id}`}
								key={user.Participation.id}
								user={user}
								{...common_gallery_props}
							/>
						)
					})}
				</ul>

				{(pagination.total_pages > 1) && (
					<nav class="gui_pagination gui_main_text_color_on_bg">
						<SquaredButton
							icon='knk-arrow-l-big'
							_class={{ other:'go_prev_page' }}
							onClick={() => changePage((pagination.page > 1) ? (pagination.page -1) : 1)}
							autotranslate={false}
							aria-label="Go to previous page"
						/>

						{pagination.pages_arr.map((page, p) => {
							const selected = (page == pagination.page) ? true : false;
							return (
								<ActionButton
									key={p}
									_class={{other:`go_${page}_page` + ((selected) ? ' selected' + ((is_main_color_bg_light) ? ' blackColor' : ' whiteColor') : ' blackColor')}}
									onClick={() => changePage(page)}
									autotranslate={false}
								>{page}</ActionButton>
							)
						})}

						<SquaredButton
							icon='knk-arrow-r-big'
							_class={{ other:'go_next_page' }}
							onClick={() => changePage((pagination.page < pagination.total_pages) ? (pagination.page + 1) : pagination.total_pages)}
							autotranslate={false}
							aria-label="Go to next page"
						/>
					</nav>
				)}

				{(galleryState.participation !== false) && (
					<SimpleVoteConfirm
						type='layer'
						promotion={promotion}
						entry={galleryState.participation}
						stage={stage}
						votingSubmit={simpleVoteSubmit}
						is_logged={is_logged}
						is_voted={galleryState.is_voted}
						login_id={login_id}
						intl={props.intl}
						locale={props.locale}
						onClose={() => dispatchGallery({ type:'TOGGLE_SIMPLE_VOTE', participation:false })}
					/>
				)}

				{(galleryState.display_message_vote_self) && (
					<FloatingLayerPublic
						pm={promotion}
						title={`<h1>${i18n("Oops")}</h1>`}
						backButton={() => dispatchGallery({ type:'TOGGLE_ADD_CART_ERROR'})}
					>
						<LeveledHTMLText>
							<p>{i18n("Sorry! You cannot vote for one of your entries.")}</p>
						</LeveledHTMLText>
					</FloatingLayerPublic>
				)}
			</Wrapper>
		</DndProvider>
	);
}

export const GalleryNoRouter = injectIntl(EntriesMultiGallery);
export default injectIntl(RouterGallery);